/**
 * True when the current session is the screensaver mode — used to gate the
 * ScreensaverOverlay mount + the ScreensaverSystem registration. The
 * `/screensaver` pathname survives coerceModeUrl (it rewrites search params,
 * not the path); the `autoCycle` param is the post-coercion signal.
 *
 * Cached once per page load (W2-2, 2026-09-29): per-entity render paths call
 * this per mob/player per frame (`MobRenderer.update` → `getMobPresentationFlags`,
 * `PlayerRenderer.updateNameTag`). The old body rebuilt
 * `new URLSearchParams(window.location.search)` on every call — the top
 * production CPU offender in the W2-3 profile.
 *
 * Invalidation contract: the mode URL is coerced at boot
 * (`applyModeUrlCoercion` is imported before any caller) and no runtime code
 * rewrites the `/screensaver` pathname or the `autoCycle` param afterwards, so
 * the value is a session constant. `__resetIsScreensaverModeForTests` is the
 * only invalidation seam (tests only).
 */
import { bootPathname, bootSearchParams } from '../boot/bootUrl';

let cached: boolean | null = null;

function computeIsScreensaverMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (bootPathname() === '/screensaver') return true;
    return bootSearchParams().get('autoCycle') === 'biomes';
  } catch {
    return false;
  }
}

export function isScreensaverMode(): boolean {
  if (cached === null) cached = computeIsScreensaverMode();
  return cached;
}

/** Test seam — drop the cached mode so specs can re-prime it from a new URL. */
export function __resetIsScreensaverModeForTests(): void {
  cached = null;
}
